<template>
  <div class="breadcrumb" :style="{ left: collapsed ? '80px' : '220px' }">
    <a-button 
      type="text" 
      class="toggle-btn" 
      @click="handleToggleCollapsed"
      title="收起侧边栏"
    >
      <a-icon :type="collapsed ? 'menu-unfold' : 'menu-fold'"></a-icon>
    </a-button>
    <div class="tab-separator">
      <div class="separator-line"></div>
    </div>
    <a-button 
      type="text" 
      class="scroll-btn left" 
      @click="scrollLeft" 
      :disabled="!canScrollLeft"
      title="滚动到最左边"
    >
      <a-icon type="double-left"></a-icon>
    </a-button>
    <div class="tab-separator">
      <div class="separator-line"></div>
    </div>
    <div class="tabs-container" ref="tabsContainer" @scroll="handleScroll">
      <div class="tabs-list" ref="tabsList">
        <template v-for="(tab, index) in tabs">
          <div class="tab-separator" v-if="index > 0" :key="'sep-' + index">
            <div class="separator-line"></div>
          </div>
          <div 
            :key="tab.path"
            class="tab-item"
            :class="{ active: isActive(tab.path) }"
            @click="handleTabClick(tab)"
          >
            <span class="tab-title">{{ tab.title }}</span>
            <div class="tab-actions">
              <a-button 
                type="text"
                class="action-btn" 
                @click.stop="handleRefresh(tab.path)"
                title="刷新"
              >
                <a-icon type="reload" class="action-icon"></a-icon>
              </a-button>
              <a-button 
                v-if="tab.path !== '/workbench' && tabs.length > 1"
                type="text"
                class="action-btn close" 
                @click.stop="handleClose(tab.path)"
                title="关闭"
              >
                <a-icon type="close" class="action-icon"></a-icon>
              </a-button>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div class="tab-separator">
      <div class="separator-line"></div>
    </div>
    <a-button 
      type="text"
      class="scroll-btn right" 
      @click="scrollRight" 
      :disabled="!canScrollRight"
      title="滚动到最右边"
    >
      <a-icon type="double-right"></a-icon>
    </a-button>
  </div>
</template>

<script>
import { useTabs } from '../composables/useTabs'

export default {
  name: 'Breadcrumb',
  props: {
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  inject: ['refreshContent'],
  data() {
    return {
      tabs: [],
      activeTab: null,
      canScrollLeft: false,
      canScrollRight: false,
      tabsContainer: null,
      tabsList: null
    }
  },
  created() {
    const { tabs, activeTab, addTab, removeTab, setActiveTab, isActive } = useTabs()
    this.tabsData = { tabs, activeTab, addTab, removeTab, setActiveTab, isActive }
  },
  mounted() {
    this.$nextTick(() => {
      this.initTabs()
      this.tabsContainer = this.$refs.tabsContainer
      this.tabsList = this.$refs.tabsList
      this.checkScroll()
      window.addEventListener('resize', this.checkScroll)
    })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScroll)
  },
  watch: {
    '$route.path': {
      handler(newPath) {
        this.handleRouteChange(newPath)
      },
      immediate: true
    }
  },
  methods: {
    initTabs() {
      if (this.tabsData.tabs.length === 0 || !this.tabsData.tabs.find(t => t.path === '/workbench')) {
        this.tabsData.addTab({
          path: '/workbench',
          title: '工作台',
          name: 'Workbench'
        })
      }
      this.handleRouteChange(this.$route.path)
    },
    handleRouteChange(path) {
      if (path !== '/') {
        const existingTab = this.tabsData.tabs.find(t => t.path === path)
        if (!existingTab && path !== '/workbench') {
          this.tabsData.addTab({
            path: path,
            title: this.$route.meta.title || '未命名',
            name: this.$route.name
          })
        }
        if (path === '/workbench') {
          this.tabsData.setActiveTab(null)
        } else {
          this.tabsData.setActiveTab(path)
        }
      }
      this.syncTabs()
    },
    syncTabs() {
      this.tabs = [...this.tabsData.tabs]
      this.activeTab = this.tabsData.activeTab
      this.$nextTick(() => {
        this.checkScroll()
      })
    },
    checkScroll() {
      if (this.tabsContainer) {
        const { scrollLeft, scrollWidth, clientWidth } = this.tabsContainer
        this.canScrollLeft = scrollLeft > 0
        this.canScrollRight = scrollLeft + clientWidth < scrollWidth - 5
      }
    },
    handleScroll() {
      this.checkScroll()
    },
    isActive(path) {
      return this.tabsData.isActive(path)
    },
    handleTabClick(tab) {
      this.tabsData.setActiveTab(tab.path)
      this.syncTabs()
      this.$router.push(tab.path).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error(err)
        }
      })
    },
    handleClose(path) {
      const currentIndex = this.tabsData.tabs.findIndex(t => t.path === path)
      this.tabsData.removeTab(path)
      this.syncTabs()
      
      if (currentIndex !== -1) {
        let targetTab = null
        const newIndex = currentIndex - 1
        if (newIndex >= 0) {
          targetTab = this.tabsData.tabs[newIndex]
        } else if (currentIndex < this.tabsData.tabs.length) {
          targetTab = this.tabsData.tabs[currentIndex]
        }
        
        if (targetTab) {
          this.$nextTick(() => {
            if (this.refreshContent) {
              this.refreshContent()
            }
            setTimeout(() => {
              this.tabsData.setActiveTab(targetTab.path)
              this.syncTabs()
              this.$router.push(targetTab.path).catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                  console.error(err)
                }
              })
            }, 50)
          })
        } else {
          this.$router.push('/workbench').catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error(err)
            }
          })
        }
      }
    },
    handleRefresh(path) {
      if (this.refreshContent) {
        this.refreshContent()
      }
    },
    scrollLeft() {
      if (this.tabsContainer && this.canScrollLeft) {
        this.tabsContainer.scrollLeft = 0
      }
    },
    scrollRight() {
      if (this.tabsContainer && this.canScrollRight) {
        if (this.tabsList) {
          this.tabsContainer.scrollLeft = this.tabsList.offsetWidth
        }
      }
    },
    handleToggleCollapsed() {
      this.$emit('toggle-collapsed')
    }
  }
}
</script>

<style scoped>
.breadcrumb {
  position: absolute;
  top: 0;
  right: 0;
  height: 48px;
  z-index: 20;
  box-shadow: inset 0px -1px 0px 0px #d7d7db, 0px 4px 8px 0px rgba(0, 0, 0, 0.08);
  background: #f2f3f5;
  display: flex;
  align-items: stretch;
  transition: left 0.3s ease;
}

.toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #f2f3f5;
  color: #5e5e66;
  transition: color 0.3s;
  flex-shrink: 0;
  padding: 0;
  font-size: 16px;
  border-radius: 0;
  border: none;
  box-shadow: inset 0px -1px 0px 0px #d7d7db;
}

.breadcrumb ::v-deep .toggle-btn.ant-btn {
  background: #f2f3f5 !important;
  border: none !important;
  box-shadow: inset 0px -1px 0px 0px #d7d7db !important;
}

.breadcrumb ::v-deep .toggle-btn.ant-btn:hover,
.breadcrumb ::v-deep .toggle-btn.ant-btn:focus {
  background: #f2f3f5 !important;
  color: #fc6732 !important;
}

.breadcrumb ::v-deep .toggle-btn.ant-btn::before {
  display: none !important;
}

.scroll-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 48px;
  background: #f2f3f5;
  color: #5e5e66;
  transition: color 0.3s;
  flex-shrink: 0;
  padding: 0;
  font-size: 14px;
  border-radius: 0;
  border: none;
  box-shadow: inset 0px -1px 0px 0px #d7d7db;
}

.breadcrumb ::v-deep .scroll-btn.ant-btn {
  background: #f2f3f5 !important;
  border: none !important;
  box-shadow: inset 0px -1px 0px 0px #d7d7db !important;
}

.breadcrumb ::v-deep .scroll-btn.ant-btn:hover:not(:disabled),
.breadcrumb ::v-deep .scroll-btn.ant-btn:focus:not(:disabled) {
  background: #f2f3f5 !important;
  color: #fc6732 !important;
}

.breadcrumb ::v-deep .scroll-btn.ant-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.breadcrumb ::v-deep .scroll-btn.ant-btn::before {
  display: none !important;
}

.tabs-container {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
}

.tabs-container::-webkit-scrollbar {
  display: none;
}

.tabs-container {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.tabs-list {
  display: flex;
  align-items: stretch;
  height: 48px;
  width: max-content;
}

.tab-separator {
  display: flex;
  align-items: center;
  padding: 0 8px;
  flex-shrink: 0;
}

.separator-line {
  width: 1px;
  height: 20px;
  background: #d7d7db;
}

.tab-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 48px;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab-item.active {
  background: #ffffff;
  box-shadow: inset 0px -3px 0px 0px #fc6732;
}

.tab-title {
  font-size: 14px;
  line-height: 22px;
  color: #5e5e66;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", SimHei, Arial, Helvetica, sans-serif;
  transition: color 0.3s;
  margin-right: 12px;
}

.tab-item.active .tab-title {
  color: #fc6732;
  font-weight: 600;
}

.tab-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  width: 0;
  overflow: hidden;
  transition: all 0.3s ease;
}

.tab-item:hover .tab-actions {
  opacity: 1;
  width: 48px;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  cursor: pointer;
  color: #B0B1B8;
  border: none !important;
  border-radius: 4px;
  transition: all 0.3s;
  font-size: 14px;
  padding: 0 !important;
  margin: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.action-btn:hover {
  color: #fc6732;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
}

.action-btn.ant-btn {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.action-btn.ant-btn:hover,
.action-btn.ant-btn:focus {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.action-icon {
  font-size: 14px;
}
</style>